import { Component, Inject, OnInit } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { MatLegacyDialogRef as MatDialogRef, MAT_LEGACY_DIALOG_DATA as MAT_DIALOG_DATA } from '@angular/material/legacy-dialog';
import { Rol, Usuario } from '../usuario.model';

export interface UsuarioFormData {
  usuario?: Usuario;
  rolesDisponibles: Rol[];
}

export interface UsuarioFormResult {
  mode: 'create' | 'edit';
  payload: any;
  passwordChanged: boolean;
  rolesChanged: boolean;
  activoChanged: boolean;
  newActivo: boolean;
  /**
   * Roles seleccionados en el momento de cerrar el diálogo (siempre presente,
   * tanto en create como en edit). En create también se duplica dentro de
   * `payload.roles` para alimentar el endpoint admin/create.
   */
  selectedRoles: number[];
}

@Component({
  selector: 'app-gu-usuario-form-dialog',
  templateUrl: './usuario-form-dialog.component.html',
  styleUrls: ['./usuario-form-dialog.component.scss']
})
export class UsuarioFormDialogComponent implements OnInit {

  form: FormGroup;
  mode: 'create' | 'edit' = 'create';
  selectedRoles = new Set<number>();
  activo = true;
  initialActivo = true;
  initialRoles = new Set<number>();
  showPasswordSection = true;

  constructor(
    private fb: FormBuilder,
    public dialogRef: MatDialogRef<UsuarioFormDialogComponent>,
    @Inject(MAT_DIALOG_DATA) public data: UsuarioFormData
  ) {}

  ngOnInit(): void {
    this.mode = this.data.usuario ? 'edit' : 'create';
    const passwordValidator = this.mode === 'create' ? [Validators.required, Validators.minLength(6)] : [];

    this.form = this.fb.group({
      nombre:           [this.data.usuario?.nombre || '', Validators.required],
      apellido1:        [this.data.usuario?.apellido1 || '', Validators.required],
      apellido2:        [this.data.usuario?.apellido2 || ''],
      numero_documento: [this.data.usuario?.numero_documento || '', Validators.required],
      email:            [this.data.usuario?.email || '', [Validators.required, Validators.email]],
      telefono:         [this.data.usuario?.telefono || ''],
      password:         ['', passwordValidator],
      confirmPassword:  ['', passwordValidator],
    });

    if (this.mode === 'edit' && this.data.usuario) {
      (this.data.usuario.rol || []).forEach(r => {
        this.selectedRoles.add(r.id);
        this.initialRoles.add(r.id);
      });
      this.activo = !!this.data.usuario.activo;
      this.initialActivo = this.activo;
    }
  }

  toggleRol(rolId: number): void {
    if (this.selectedRoles.has(rolId)) this.selectedRoles.delete(rolId);
    else this.selectedRoles.add(rolId);
  }

  isRolSelected(rolId: number): boolean { return this.selectedRoles.has(rolId); }

  toggleActivo(): void { this.activo = !this.activo; }

  cancel(): void { this.dialogRef.close(null); }

  passwordsMatch(): boolean {
    const p = this.form.get('password')?.value || '';
    const c = this.form.get('confirmPassword')?.value || '';
    return p === c;
  }

  passwordFilled(): boolean {
    return !!(this.form.get('password')?.value || '').trim();
  }

  formIsValid(): boolean {
    if (!this.form.valid) return false;
    if (this.selectedRoles.size === 0) return false;
    if (this.mode === 'create' && !this.passwordsMatch()) return false;
    if (this.mode === 'edit' && this.passwordFilled() && !this.passwordsMatch()) return false;
    return true;
  }

  rolesChanged(): boolean {
    if (this.selectedRoles.size !== this.initialRoles.size) return true;
    for (const id of this.selectedRoles) if (!this.initialRoles.has(id)) return true;
    return false;
  }

  save(): void {
    if (!this.formIsValid()) return;
    const v = this.form.value;
    const passwordChanged = this.mode === 'create' || this.passwordFilled();

    const payload: any = {
      nombre: v.nombre,
      apellido1: v.apellido1,
      apellido2: v.apellido2 || null,
      numero_documento: v.numero_documento,
      email: v.email,
      telefono: v.telefono || null,
    };
    if (this.mode === 'create') {
      payload.password = v.password;
      payload.roles = Array.from(this.selectedRoles);
    } else if (passwordChanged) {
      payload.password = v.password;
    }

    const result: UsuarioFormResult = {
      mode: this.mode,
      payload,
      passwordChanged,
      rolesChanged: this.rolesChanged(),
      activoChanged: this.activo !== this.initialActivo,
      newActivo: this.activo,
      selectedRoles: Array.from(this.selectedRoles),
    };
    this.dialogRef.close(result);
  }

  get rolesAsArray(): number[] { return Array.from(this.selectedRoles); }
}
